<template>
  <div class="flex flex-column gap-3">
    <associate-shifts-filters :current-filters.sync="filterNew" :visible.sync="visibleFiltersModal" />

    <shift-sign :info="signingShift" :visible.sync="visibleSignModal" @edit="afterSignShift" />

    <table-layout>
      <template #header>
        <h3 class="m-0">Shifts</h3>
      </template>

      <template #controls-left>
        <shift-table-controls
          :initial-values="{
            associate_id: associate.id,
            associateNumber: associate.associate_number,
            start: filterNew?.start_date,
            end: filterNew?.end_date
          }"
          @edit="loadData"
        />
      </template>

      <template #controls-right>
        <a-badge :count="countFilters">
          <a-button icon="filter" @click="visibleFiltersModal = true"> Filters </a-button>
        </a-badge>
        <a-button class="flex align-items-center justify-content-center" @click="loadData" title="Refresh Page">
          <a-icon width="20" height="20" icon="solar:refresh-square-broken" />
        </a-button>
      </template>

      <template #filters>
        <active-table-filters
          :items="activeFilterTags"
          @edit="openFilterEditor"
          @remove="removeFilter"
          @reset="resetFilters"
        />
      </template>

      <a-table
        size="small"
        :columns="
          extendedColumns.filter(item => {
            if (item.key === 'company' && !isStaff) return false

            return true
          })
        "
        :rowKey="record => record.id"
        :data-source="shifts"
        :loading="loading"
        :style="{ whiteSpace: 'pre' }"
        :locale="{ emptyText: getNoShiftsMessage }"
        :pagination="pagination"
        :dropdown-match-select-width="false"
        :scroll="{ x: 1400, y: 450 }"
        :rowClassName="getRowClassName"
        @change="handleTableChange"
        :customRow="
          record => ({
            on: {
              click: () => openShiftModal(record.id)
            }
          })
        "
      >
        <span slot="associate" slot-scope="text, record">
          <grouped-associate-info
            :first-name="record.internal_payment_info.associate.first_name"
            :last-name="record.internal_payment_info.associate.last_name"
            :photo-url="record.internal_payment_info.associate.photo_url"
            :unverified="!record.internal_payment_info.associate.is_verified"
            :role="record.internal_payment_info.associate.role.name"
            :payment-info="{
              payment_type: record.internal_payment_info.payment_type,
              method: record.internal_payment_info.pay_method,
              legal_entity: record.internal_payment_info.legal_entity
            }"
            :associate-number="record.internal_payment_info.associate.associate_number"
            :scheduled-type="record.internal_payment_info.associate.schedule_type"
          />
        </span>
        <span slot="company" slot-scope="text, record">
          <a-tag v-if="!record?.external_payment_info" class="auth-tag" color="#10b981">
            {{ record?.internal_payment_info?.project?.name }}
          </a-tag>
          <a-tag v-else color="#3b82f6" :style="{ maxWidth: '150px', textWrap: 'initial' }">
            {{ record?.external_payment_info?.project?.name }}
          </a-tag>
        </span>
        <span class="tags-cell" slot="status" slot-scope="text, record">
          <shift-status :shift="record" @handleClick="key => handleFlagClick(record, key)" />
        </span>
        <span id="#actions-btn-group" slot="actions" slot-scope="text, record" class="flex gap-2">
          <button-more :record="record" :menu-items="menuItems" />
        </span>
      </a-table>
    </table-layout>
  </div>
</template>

<script>
import { Button, Table, Tag, notification, Badge } from 'ant-design-vue'
import { statusTagColors } from '@/const'
import GroupedAssociateInfo from '@/components/ui/grouped-associate-info.vue'
import { createNamespacedHelpers } from 'vuex'
import shiftAuths from '@/helpers/shift-auths'
import permissionMixin from '@/mixins/permissionMixin'
import featureMixin from '@/mixins/featureMixin'
import ShiftSignModal from '@/modules/shifts/components/modal/shift-sign-modal.vue'
import ShiftsTableMixin from '@/modules/shifts/mixins/ShiftsTableMixin'
import ShiftTableStatus from '@/modules/shifts/components/shift-table-status.vue'
import ProfileViewShiftFilters, { PROFILE_SHIFTS_FILTER_ORDER } from './profile-view-shift-filters.vue'
import ShiftTableControls from '@/modules/shifts/components/table/shift-table-controls.vue'

const { mapActions: shiftActions } = createNamespacedHelpers('shifts')
const { mapState: profileState } = createNamespacedHelpers('profile')
const { mapActions: modalActions } = createNamespacedHelpers('modals')

const excludedStatuses = ['deleted', 'missed', 'cancelled', 'completed', 'started', 'scheduled']

const excludedPaymentStatuses = ['not_paid', 'paid', 'in_progress', 'partially_paid', 'draft', 'cancelled']

export default {
  components: {
    'a-table': Table,
    'a-tag': Tag,
    'a-button': Button,
    'a-badge': Badge,
    'shift-table-controls': ShiftTableControls,
    'associate-shifts-filters': ProfileViewShiftFilters,
    'grouped-associate-info': GroupedAssociateInfo,
    'shift-sign': ShiftSignModal,
    'shift-status': ShiftTableStatus
  },

  mixins: [ShiftsTableMixin, permissionMixin, featureMixin],
  data() {
    return {
      shifts: [],
      filterNew: {
        shift_statuses: [...excludedStatuses],
        shift_payment_statuses: [...excludedPaymentStatuses]
      },

      visibleSignModal: false,
      visibleFiltersModal: false,

      signingShift: undefined,

      loading: false,
      requestKey: `shifts-table-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
    }
  },

  computed: {
    ...profileState({
      associate: state => state.associate
    }),

    menuItems() {
      return [
        {
          key: 'view-shift',
          icon: 'fluent:open-20-regular',
          label: 'Open Shift',
          visible: true,
          handler: record => {
            this.openShiftModal(record.id)
          }
        },
        {
          key: 'send-message',
          icon: 'carbon:chat',
          label: 'Send Message',
          visible: this.hasPermission('send_push_notification'),
          handler: record => {
            this.openSmsSendModal(record.internal_payment_info.associate)
          }
        }
      ]
    },

    extendedColumns() {
      return this.columns.filter(column => !['associate'].includes(column.key))
    },

    getNoShiftsMessage() {
      const appliedFilters = Object.entries(this.filterNew)
        .filter(([key, value]) => value && value.length > 0)
        .map(([key]) => key)
        .join(', ')

      return `No ${
        appliedFilters.length > 0 ? appliedFilters : 'shifts'
      } here yet. The selected filters might have caused this. Try adjusting your search!`
    },

    statusTagColors() {
      return statusTagColors
    },

    tableFilterMeta() {
      return {
        shift_statuses: {
          predefined: true,
          value: excludedStatuses
        },
        shift_payment_statuses: {
          predefined: true,
          value: excludedPaymentStatuses
        }
      }
    },

    tableResetFilters() {
      return {
        shift_statuses: [...excludedStatuses],
        shift_payment_statuses: [...excludedPaymentStatuses]
      }
    },

    tableFilterOrder() {
      return PROFILE_SHIFTS_FILTER_ORDER
    },
  },
  watch: {
    filterNew: {
      handler(newVal, prevVal) {
        if (JSON.stringify(newVal) != JSON.stringify(prevVal)) {
          this.pagination.current = 1
          this.loadData()
        }
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    ...modalActions(['openModal']),
    ...shiftActions(['getShifts']),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    convertType: shiftAuths.convertType,

    handleFlagClick(record, key) {
      switch (key) {
        case 'hasAuth':
          this.openShiftModal(record.id, 'authorizations')
          break
        case 'notSigned':
          this.visibleSignModal = true
          this.signingShift = {
            shift_id: record.id,
            associate_number: record.internal_payment_info.associate.associate_number
          }
          break
        case 'cdphMissing':
        case 'digitalCdphMissing':
        case 'digitalCdphNotSigned':
          this.openShiftModal(record.id, 'confirmation')
          break
      }
    },

    openShiftModal(shift_id, open_tab = 'info') {
      this.openModal({
        type: 'shiftView',
        data: {
          visible: true,
          edit: () => this.loadData(),
          shift_id,
          open_tab,
          shifts_list: this.shifts.map(item => item.id)
        }
      })
    },

    afterSignShift() {
      this.signingShift = undefined
      this.loadData()
    },

    openSmsSendModal(record) {
      this.openModal({
        type: 'notification',
        data: {
          visible: true,
          associate_number: record.associate_number
        }
      })
    },

    handleTableChange(pagination) {
      const isPaginationChanged =
        pagination.current !== this.pagination.current || pagination.pageSize !== this.pagination.pageSize

      if (!isPaginationChanged) {
        return
      }

      this.pagination = { ...pagination }

      this.loadData()
    },

    async loadData() {
      const payload = {
        page: this.pagination.current,
        size: this.pagination.pageSize,
        filter: { ...this.filterNew, associates: [this.associate.id] },
        requestGroup: this.requestKey
      }

      this.loading = true
      let skipFinally = false

      try {
        const data = await this.getShifts(payload)

        this.shifts = data.items
        this.pagination.total = data.total_count
      } catch (error) {
        if (error === 'canceled') {
          skipFinally = true
          this.loading = true
          return
        }
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        if (!skipFinally) this.loading = false
      }
    }
  }
}
</script>
